import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { workspaceService } from "../../services/api";
import { workspaces } from "./workspaces";
export default function WorkspaceSearch() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const dialog = useRef(null);
  const input = useRef(null);
  const location = useLocation();
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);
  useEffect(() => {
    const show = () => setOpen(true);
    const key = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("timelyzer:search", show);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("timelyzer:search", show);
      window.removeEventListener("keydown", key);
    };
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [location.key]);
  useEffect(() => {
    if (open) {
      dialog.current?.showModal?.();
      input.current?.focus();
    } else dialog.current?.close?.();
  }, [open]);
  const results = useQuery({
    queryKey: ["workspace-search", query],
    queryFn: () => workspaceService.search(query),
    enabled: open && query.length > 0,
    retry: false,
  });
  return (
    <>
      <button
        className="mobile-search-button"
        aria-label="Search everything"
        onClick={() => setOpen(true)}
      >
        <MagnifyingGlassIcon />
      </button>
      <dialog
        ref={dialog}
        className="workspace-search-dialog"
        aria-label="Search everything"
        onCancel={() => setOpen(false)}
        onClick={(e) => {
          if (e.target === dialog.current) setOpen(false);
        }}
      >
        <div className="search-dialog-inner">
          <div className="search-dialog-heading">
            <h2>Find anything</h2>
            <button onClick={() => setOpen(false)} aria-label="Close search">
              <XMarkIcon />
            </button>
          </div>
          <input
            className="field-input"
            ref={input}
            aria-label="Search saved work"
            placeholder="Notes, people, goals, conversations…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="search-results" aria-live="polite">
            {!query ? (
              <>
                {workspaces.slice(0, 6).map((w) => (
                  <Link key={w.id} to={w.to}>
                    <span>{w.label}</span>
                    <small>Workspace</small>
                  </Link>
                ))}
              </>
            ) : results.isPending ? (
              <p role="status">Searching your saved work…</p>
            ) : results.error ? (
              <p role="alert">
                Search couldn’t load.{" "}
                <button onClick={() => results.refetch()}>Try again</button>
              </p>
            ) : results.data?.items.length ? (
              results.data.items.map((row) => (
                <Link key={row.key} to={row.link}>
                  <span>{row.title}</span>
                  <small>{row.kind}</small>
                </Link>
              ))
            ) : (
              <p>No saved results match “{query}”. Try a different phrase.</p>
            )}
          </div>
        </div>
      </dialog>
    </>
  );
}
